/**
 * TRIM / EXTEND geometry for NON-Line targets and cutting/boundary edges.
 *
 * The Batch-5 helpers in {@link ./geometryTransforms} only solved the
 * Line-vs-{Line,Circle,Arc} cases and only ever trimmed/extended a **Line**
 * target. AutoCAD's TRIM/EXTEND operate on the full curve family — Arc,
 * Circle, Ellipse, LWPOLYLINE, Spline — as BOTH the object being cut AND as
 * cutting/boundary edges. This module supplies the parametric trim/extend of
 * Arc / Circle / Ellipse targets, keeping the result as TYPED geometry
 * (angle / param ranges), never a flattened polyline. The curve×curve
 * intersection math (circle×circle, arc×arc, line×ellipse, …) lives in
 * `trimExtendGeometryCore.ts`; entity tessellation and the unified
 * intersection dispatcher (`entityIntersectionPoints`) live in
 * `trimExtendIntersection.ts` — both re-exported here (Codex round-15
 * static-analysis gate: this file exceeded the project's 2000-line hard
 * ceiling after 15 rounds of local Codex review hardening; the split keeps
 * every existing import path, including the package barrel
 * `@dxf-ai-editor/command-engine`, unchanged) so this remains the single
 * entry point external code imports from.
 *
 * ## AutoCAD parity notes
 *
 * - **pick = keep**: consistent with the existing Line implementation
 *   (`trimLineAtPoint(start,end,cut,keepSide=pickPoint)` — the surviving
 *   segment is the one CONTAINING the pick; see
 *   `geometryReducerEditTrim.mutations.test.ts`). Every curve target here
 *   follows the same convention so the whole feature is internally
 *   consistent. (The command help text documents the AutoCAD "pick = remove"
 *   convention; reconciling the two is a separate cross-cutting concern that
 *   would flip the existing Line tests + the draft-modify state machine and is
 *   intentionally out of this slice's scope.)
 * - **edge extent = actual** (AutoCAD default "Edge = No extend"): a cut point
 *   must lie on the cutting edge's real extent (line segment, arc range, ellipse
 *   range, polyline/spline span). Edge-mode = Extend (project the edge to
 *   infinity) is a follow-up flag.
 * - **Circle → Arc**: trimming a closed circle yields an open Arc, matching
 *   AutoCAD (a new object replaces the circle).
 * - **exactness**: Line/Circle/Arc/Ellipse pairs use closed-form intersection.
 *   Combinations that involve a Spline, a bulge (arc) polyline segment against
 *   another curve, or an Ellipse against a circular/elliptic edge fall back to
 *   a fine adaptive tessellation (documented at the call site) — accurate to
 *   the sample density, which is the same technique AutoCAD uses internally for
 *   spline edge picking.
 */

import type { EllipseEntity } from "@dxf-ai-editor/drawing-model";

import {
  angleDegOf,
  ARC_RANGE_TOL_DEG,
  ellipseFrame,
  ellipseParamOf,
  ellipsePointAtParam,
  ELLIPSE_RANGE_TOL_RAD,
  GEO_EPS,
  normDeg,
  normRad,
  sweepDeg,
  sweepRad,
  type ArcGeom,
  type IntPoint,
} from "./trimExtendGeometryCore";

export * from "./trimExtendGeometryCore";
// Codex round-15 static-analysis gate (see module doc comment above): entity
// tessellation + the unified intersection dispatcher moved to
// `trimExtendIntersection.ts`, re-exported here so every existing import
// path (`from "./trimExtendGeometry"`, and the package barrel
// `@dxf-ai-editor/command-engine`) keeps working unchanged.
export {
  entityIntersectionPoints,
  entityToPolyline,
  type IntersectOptions,
} from "./trimExtendIntersection";

// ── Parametric span selection (shared TRIM core) ───────────────────

/**
 * Given the parameters of the cut points and the pick along a curve, return the
 * surviving span(s) as `[lo, hi]` parameter ranges, honouring the "pick = keep"
 * convention: the survivor is the single span that CONTAINS the pick, bounded
 * by the two nearest cuts straddling it (or the curve's own ends when open).
 *
 * - `openDomain` present → the curve is open ([t0, t1]); the pick span may be
 *   bounded by an endpoint on either side, so it survives as ONE range and the
 *   REST of the curve survives as up-to-one more range on the far side of the
 *   removed complement. Because a single click removes only the complement of
 *   the pick span, an OPEN curve yields the pick span PLUS the two outer
 *   remainders — i.e. the classic AutoCAD "trim the middle → two pieces".
 * - `openDomain` absent → the curve is closed (period `period`); ≥2 cuts are
 *   required and the survivor is the single arc containing the pick.
 */
/**
 * Sort + collapse coincident parameters on a CLOSED domain of length `period`.
 * Two parameters are the same cut when their circular gap ≤ a tolerance scaled
 * to the domain (so it works uniformly for degrees / radians / arc-length), and
 * the wraparound pair (near 0 and near `period`) is the same seam point. Real
 * crossings on a closed curve are far apart, so this only removes duplicates of
 * one geometric point, never distinct cuts.
 */
function dedupeCircularParams(sorted: number[], period: number): number[] {
  if (sorted.length === 0) return [];
  const asc = [...sorted].sort((a, b) => a - b);
  const tol = Math.max(GEO_EPS, period * 1e-9);
  const out: number[] = [];
  for (const c of asc) {
    if (out.length === 0 || Math.abs(c - out[out.length - 1]!) > tol) {
      out.push(c);
    }
  }
  // Seam: the smallest and largest may be the same point across 0/period.
  if (out.length >= 2) {
    const first = out[0]!;
    const last = out[out.length - 1]!;
    if (period - last + first <= tol) out.pop();
  }
  return out;
}

/**
 * Project a raw pick parameter onto an OPEN target's extent [t0, t1]. A pick
 * already inside the domain is returned unchanged. Outside, the projection
 * depends on the domain topology (Codex #497 P2):
 *
 * - CIRCULAR domain (`period` > domain length — an ARC on a `period`-degree /
 *   `2π`-radian circle, or an elliptic arc): snap to the endpoint that is
 *   angularly nearer AROUND the circle. A click just BEFORE the start has a
 *   param that wraps near `period`, so it must resolve to `t0` — NOT the
 *   numerically-nearer `t1`. The forward gap from the end (`t1`→pick) is
 *   compared against the closing gap (pick→`t0`) measured modulo `period`.
 * - LINEAR domain (`period` === domain length — a line segment): the nearest
 *   endpoint is the plain min/max clamp.
 *
 * This lets a within-selection-tolerance click just past an endpoint (the scene
 * passes RAW mouse coordinates as the pick) trim the adjacent stub instead of
 * silently no-oping.
 */
function clampPickToOpenDomain(
  pick: number,
  openDomain: { t0: number; t1: number },
  period: number,
): number {
  const { t0, t1 } = openDomain;
  if (pick >= t0 - GEO_EPS && pick <= t1 + GEO_EPS) return pick;
  const domainLen = t1 - t0;
  if (period > domainLen + GEO_EPS) {
    // Circular: normalise the wrap, then measure the two directed gaps.
    const wrap = (x: number): number => ((x % period) + period) % period;
    const p = wrap(pick);
    if (p >= t0 && p <= t1) return p; // lands in-domain once the wrap is removed
    const gapPastEnd = wrap(p - t1); // t1 → pick, going into the outside gap
    const gapBeforeStart = wrap(t0 - p); // pick → t0, closing the gap
    return gapPastEnd <= gapBeforeStart ? t1 : t0;
  }
  // Linear segment: nearest endpoint is the numeric clamp.
  return Math.min(Math.max(pick, t0), t1);
}

export function selectTrimSurvivors(
  cutParams: number[],
  pickParam: number,
  period: number,
  openDomain: { t0: number; t1: number } | null,
): Array<[number, number]> {
  if (openDomain) {
    // Boundaries = sorted unique cuts strictly inside the domain, plus the ends.
    const inside = cutParams
      .filter((c) => c > openDomain.t0 + GEO_EPS && c < openDomain.t1 - GEO_EPS)
      .sort((a, b) => a - b);
    // No interior crossing on the target → nothing to trim against (an edge
    // that only grazes an endpoint must NOT delete the whole open curve).
    if (inside.length === 0) return [];
    const bounds = [openDomain.t0, ...inside, openDomain.t1];
    // PROJECT / CLAMP a pick that fell just OUTSIDE the extent back onto it
    // BEFORE choosing the remove span (Codex #497 P2). Without this, a raw
    // within-tolerance click just past an endpoint yields a param outside
    // [t0,t1] and TRIM silently no-ops.
    const pick = clampPickToOpenDomain(pickParam, openDomain, period);
    // Find the interval [lo,hi] containing the pick → REMOVE it; keep the rest.
    let removeIdx = -1;
    for (let i = 0; i + 1 < bounds.length; i++) {
      if (pick >= bounds[i]! - GEO_EPS && pick <= bounds[i + 1]! + GEO_EPS) {
        removeIdx = i;
        break;
      }
    }
    if (removeIdx < 0) return []; // genuinely unresolvable pick → no-op
    const survivors: Array<[number, number]> = [];
    if (bounds[removeIdx]! - openDomain.t0 > GEO_EPS) {
      survivors.push([openDomain.t0, bounds[removeIdx]!]);
    }
    if (openDomain.t1 - bounds[removeIdx + 1]! > GEO_EPS) {
      survivors.push([bounds[removeIdx + 1]!, openDomain.t1]);
    }
    return survivors;
  }

  // Closed curve: parameters live in [0, period). Sort the cuts and find the
  // pair [lo, hi] immediately straddling the pick — that span is REMOVED (pick =
  // remove), so the single surviving arc is the COMPLEMENT [hi, lo] (wrapping
  // the long way, through any intermediate cuts, which a single click leaves
  // untouched).
  //
  // Deduplicate COINCIDENT cut parameters (within tolerance, wrapping at the
  // 0/period seam) BEFORE the ≥2 requirement: a tessellated polyline/spline
  // cutter grazing a shared vertex/seam, or a tangent edge, can report the SAME
  // geometric crossing twice. Left as two "distinct" cuts they falsely satisfy
  // `cuts.length >= 2` and select a degenerate/near-full span (replacing a
  // circle with a full arc) instead of no-oping.
  const cuts = dedupeCircularParams(
    cutParams.map((c) => ((c % period) + period) % period),
    period,
  );
  if (cuts.length < 2) return [];
  const pk = ((pickParam % period) + period) % period;
  // EQUIVALENT MUTANT: mutator EqualityOperator, `i < cuts.length` ->
  // `i <= cuts.length`. The `dedupeCircularParams`-cleaned `cuts` (>=2
  // distinct params, sorted) partition the WHOLE circle into
  // `cuts.length` cyclic spans `[cuts[i], cuts[(i+1)%cuts.length]]` — that
  // partition is total and exhaustive by construction (every point on the
  // circle lies in exactly one span, including boundaries within the
  // epsilon tolerance), so `pk` is GUARANTEED to satisfy `inSpan` for some
  // `i` in `[0, cuts.length)` and the loop always `return`s before or at
  // `i === cuts.length - 1`. The `i === cuts.length` iteration an `<=`
  // mutant would add is therefore NEVER REACHED for any input that passes
  // the `cuts.length < 2` guard above — there is no `pk` for which
  // execution reaches that phantom iteration, so no test can observe it.
  // Stryker disable next-line EqualityOperator: cuts partition the whole circle exhaustively, so the loop always returns before the phantom i===cuts.length iteration a `<=` mutant would add (see equivalent-mutant note above)
  for (let i = 0; i < cuts.length; i++) {
    const lo = cuts[i]!;
    const hi = cuts[(i + 1) % cuts.length]!;
    const inSpan =
      hi > lo ? pk >= lo - GEO_EPS && pk <= hi + GEO_EPS : pk >= lo - GEO_EPS || pk <= hi + GEO_EPS;
    if (inSpan) return [[hi, lo]];
  }
  return [];
}

// ── Curve target trim/extend ───────────────────────────────────────

/**
 * Trim an ARC target. Returns the surviving arc angle-range(s) (degrees). A
 * mid-arc trim splits into two arcs; a near-end trim shortens to one.
 */
export function trimArcTarget(
  arc: ArcGeom,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): Array<{ startAngle: number; endAngle: number }> {
  const total = sweepDeg(arc.startAngle, arc.endAngle);
  // A full-sweep Arc (`startAngle:0,endAngle:360`, which other app helpers
  // explicitly support, or any over-full span reduced by `sweepDeg`) is a CLOSED
  // curve — route it through the closed-curve selection path like a full Ellipse
  // / a Circle (Codex #497 P2). The open path would treat 0° and 360° as distinct
  // endpoints, so cuts at 90°/270° with a pick at the 0° seam remove only [0,90]
  // instead of the closed span crossing the seam (keep [90,270]).
  //
  // EQUIVALENT MUTANT (this `if`'s own EqualityOperator `>=`->`>` and
  // ArithmeticOperator `360-GEO_EPS`->`360+GEO_EPS` mutants): `total`
  // comes ONLY from `sweepDeg`, whose OWN over-full guard
  // (`trimExtendGeometryCore.ts`'s `sweepDeg`) already collapses ANY raw
  // sweep `>= 360-GEO_EPS` to EXACTLY `360` — so `total` can only ever be
  // (a) exactly `360`, or (b) `normDeg(...)`'s output, which is always
  // `< 360-GEO_EPS` (the very condition that would otherwise trigger the
  // collapse). `total` therefore NEVER lands in the ambiguous gap
  // `[360-GEO_EPS, 360)` that would distinguish `>=` from `>`, or a
  // `-GEO_EPS` from a `+GEO_EPS` threshold shift — for `total===360`
  // both variants agree (comfortably true); for `total<360-GEO_EPS` both
  // agree (comfortably false). No test can kill these three mutants at
  // this specific site.
  // Stryker disable next-line EqualityOperator,ArithmeticOperator: sweepDeg's own over-full guard guarantees `total` is either exactly 360 or comfortably below 360-GEO_EPS, never landing in the ambiguous gap between (see equivalent-mutant note above)
  if (total >= 360 - GEO_EPS) {
    const cuts = cutPoints.map((p) => angleDegOf(arc.center, p));
    const survivors = selectTrimSurvivors(
      cuts,
      angleDegOf(arc.center, pickPoint),
      360,
      null,
    );
    if (survivors.length === 0) return [];
    const [lo, hi] = survivors[0]!;
    return [{ startAngle: normDeg(lo), endAngle: normDeg(hi) }];
  }
  const toParam = (p: IntPoint): number =>
    normDeg(angleDegOf(arc.center, p) - arc.startAngle);
  const cuts = cutPoints
    .map(toParam)
    .filter((s) => s > GEO_EPS && s < total - GEO_EPS);
  const pick = toParam(pickPoint);
  // period = 360 (the full circle the arc lives on) so an out-of-extent pick is
  // projected onto the nearer arc endpoint AROUND the circle, not clamped
  // linearly (Codex #497 P2).
  const survivors = selectTrimSurvivors(cuts, pick, 360, { t0: 0, t1: total });
  return survivors.map(([lo, hi]) => ({
    startAngle: normDeg(arc.startAngle + lo),
    endAngle: normDeg(arc.startAngle + hi),
  }));
}

/**
 * Trim a CIRCLE target → a single Arc (the span containing the pick, bounded by
 * the two nearest cut points). Returns `null` when fewer than two cuts exist.
 */
export function trimCircleTarget(
  center: IntPoint,
  radius: number,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): { startAngle: number; endAngle: number } | null {
  const cuts = cutPoints.map((p) => angleDegOf(center, p));
  const pick = angleDegOf(center, pickPoint);
  const survivors = selectTrimSurvivors(cuts, pick, 360, null);
  if (survivors.length === 0) return null;
  const [lo, hi] = survivors[0]!;
  return { startAngle: normDeg(lo), endAngle: normDeg(hi) };
}

/**
 * Extend an ARC target to a boundary. The end nearest the pick moves out to the
 * closest boundary crossing that lies BEYOND that end. Returns the new
 * angle-range, or `null` when no reachable boundary exists.
 */
export function extendArcTarget(
  arc: ArcGeom,
  boundaryPoints: IntPoint[],
  pickPoint: IntPoint,
): { startAngle: number; endAngle: number } | null {
  if (boundaryPoints.length === 0) return null;
  // Choose the end to grow by EUCLIDEAN distance from the pick to each REAL
  // endpoint, matching the Line EXTEND path (Codex #497 P2). One-way angular
  // distance only identifies the nearest endpoint when the pick lies inside the
  // current sweep: EXTEND receives the raw click point, so for a 0→90 arc a
  // click just below the start endpoint has pickAngle≈359, making the one-way
  // `distToStart`≈359 while `distToEnd`≈91 — the old code grew the WRONG end.
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const startPt: IntPoint = {
    x: arc.center.x + arc.radius * Math.cos(toRad(arc.startAngle)),
    y: arc.center.y + arc.radius * Math.sin(toRad(arc.startAngle)),
  };
  const endPt: IntPoint = {
    x: arc.center.x + arc.radius * Math.cos(toRad(arc.endAngle)),
    y: arc.center.y + arc.radius * Math.sin(toRad(arc.endAngle)),
  };
  const distToStart = Math.hypot(pickPoint.x - startPt.x, pickPoint.y - startPt.y);
  const distToEnd = Math.hypot(pickPoint.x - endPt.x, pickPoint.y - endPt.y);
  const extendEnd = distToEnd <= distToStart;
  // The extension may only sweep across the OUTSIDE gap (the complement of the
  // current arc). A candidate whose `delta` reaches or exceeds that gap has
  // wrapped past the opposite endpoint — it lies INSIDE the current sweep, so
  // honouring it would SHRINK the arc rather than extend it. Reject those.
  const gap = 360 - sweepDeg(arc.startAngle, arc.endAngle);

  let best: number | null = null; // additional sweep beyond the moving end
  let bestAngle = 0;
  for (const bp of boundaryPoints) {
    const bAngle = angleDegOf(arc.center, bp);
    // Sweep to move the chosen end onto the boundary point.
    const delta = extendEnd
      ? normDeg(bAngle - arc.endAngle) // grow endAngle CCW
      : normDeg(arc.startAngle - bAngle); // grow startAngle CW
    if (delta < GEO_EPS) continue; // not beyond the end (would shorten)
    if (delta > gap - ARC_RANGE_TOL_DEG) continue; // wrapped into the arc interior
    if (best === null || delta < best) {
      best = delta;
      bestAngle = bAngle;
    }
  }
  if (best === null) return null;
  return extendEnd
    ? { startAngle: arc.startAngle, endAngle: normDeg(bestAngle) }
    : { startAngle: normDeg(bestAngle), endAngle: arc.endAngle };
}

/**
 * Trim an ELLIPSE target. Returns the surviving elliptic-arc parameter range(s)
 * (radians). A full ellipse (0..2π) is closed → one survivor; an elliptic arc
 * is open and may split into two.
 */
export function trimEllipseTarget(
  ell: EllipseEntity,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): Array<{ startParam: number; endParam: number }> {
  const f = ellipseFrame(ell);
  // EQUIVALENT MUTANT (this line's own EqualityOperator `>=`->`>` and
  // ArithmeticOperator `2*Math.PI-GEO_EPS`->`2*Math.PI+GEO_EPS` mutants):
  // same reasoning as `trimArcTarget`'s analogous `total>=360-GEO_EPS`
  // guard — `sweepRad`'s OWN over-full guard already collapses any raw
  // sweep `>= 2*Math.PI-GEO_EPS` to EXACTLY `2*Math.PI`, so this
  // expression's LHS is always either exactly `2*Math.PI` (comfortably
  // `>=` either threshold) or `< 2*Math.PI-GEO_EPS` (comfortably `<`
  // either threshold) — never landing in the ambiguous gap between. No
  // test can kill these mutants at this specific site.
  // Stryker disable next-line EqualityOperator,ArithmeticOperator: sweepRad's own over-full guard guarantees this is either exactly 2*PI or comfortably below 2*PI-GEO_EPS, never in the ambiguous gap between (see equivalent-mutant note above)
  const isFull = sweepRad(ell.startParam, ell.endParam) >= 2 * Math.PI - GEO_EPS;
  const toWrapped = (p: IntPoint): number =>
    normRad(ellipseParamOf(f, p) - ell.startParam);
  const total = sweepRad(ell.startParam, ell.endParam);
  const pick = toWrapped(pickPoint);

  if (isFull) {
    const cuts = cutPoints.map((p) => normRad(ellipseParamOf(f, p)));
    const survivors = selectTrimSurvivors(cuts, normRad(ellipseParamOf(f, pickPoint)), 2 * Math.PI, null);
    if (survivors.length === 0) return [];
    const [lo, hi] = survivors[0]!;
    return [{ startParam: normRad(lo), endParam: normRad(hi) }];
  }

  const cuts = cutPoints
    .map(toWrapped)
    .filter((s) => s > GEO_EPS && s < total - GEO_EPS);
  // period = 2π (the full ellipse the arc lives on) so an out-of-extent pick is
  // projected onto the nearer elliptic-arc endpoint around the curve (Codex
  // #497 P2), matching trimArcTarget.
  const survivors = selectTrimSurvivors(cuts, pick, 2 * Math.PI, { t0: 0, t1: total });
  return survivors.map(([lo, hi]) => ({
    startParam: normRad(ell.startParam + lo),
    endParam: normRad(ell.startParam + hi),
  }));
}

/**
 * Extend an ELLIPTIC ARC target to a boundary. A closed (full) ellipse cannot
 * be extended and returns `null`.
 */
export function extendEllipseTarget(
  ell: EllipseEntity,
  boundaryPoints: IntPoint[],
  pickPoint: IntPoint,
): { startParam: number; endParam: number } | null {
  const f = ellipseFrame(ell);
  const total = sweepRad(ell.startParam, ell.endParam);
  // EQUIVALENT MUTANT: same reasoning as `trimEllipseTarget`'s analogous
  // `isFull` check above — `sweepRad`'s own over-full guard guarantees
  // `total` is either exactly `2*Math.PI` or comfortably below
  // `2*Math.PI-GEO_EPS`, never landing in the ambiguous gap this line's
  // EqualityOperator/ArithmeticOperator mutants would need to distinguish.
  // Stryker disable next-line EqualityOperator,ArithmeticOperator: sweepRad's own over-full guard guarantees this is either exactly 2*PI or comfortably below 2*PI-GEO_EPS (see equivalent-mutant note above and trimEllipseTarget's isFull check)
  if (total >= 2 * Math.PI - GEO_EPS) return null;
  if (boundaryPoints.length === 0) return null;
  // Choose the end to grow by EUCLIDEAN distance to each real endpoint (Codex
  // #497 P2) — same failure mode as extendArcTarget: one-way angular distance
  // picks the wrong end when the raw click lands in the outside gap.
  const startPt = ellipsePointAtParam(ell, ell.startParam);
  const endPt = ellipsePointAtParam(ell, ell.endParam);
  const distToStart = Math.hypot(pickPoint.x - startPt.x, pickPoint.y - startPt.y);
  const distToEnd = Math.hypot(pickPoint.x - endPt.x, pickPoint.y - endPt.y);
  const extendEnd = distToEnd <= distToStart;
  // Same wraparound guard as extendArcTarget: a boundary param inside the
  // current elliptic-arc span but "behind" the picked end becomes a near-2π
  // positive delta. Only the OUTSIDE gap (2π − current sweep) is a valid
  // extension; reject anything reaching/exceeding it so EXTEND no-ops instead of
  // shrinking or nearly wrapping the ellipse.
  const gap = 2 * Math.PI - total;

  let best: number | null = null;
  let bestParam = 0;
  for (const bp of boundaryPoints) {
    const bParam = ellipseParamOf(f, bp);
    const delta = extendEnd
      ? normRad(bParam - ell.endParam)
      : normRad(ell.startParam - bParam);
    if (delta < GEO_EPS) continue;
    if (delta > gap - ELLIPSE_RANGE_TOL_RAD) continue; // wrapped into the arc interior
    if (best === null || delta < best) {
      best = delta;
      bestParam = bParam;
    }
  }
  if (best === null) return null;
  return extendEnd
    ? { startParam: ell.startParam, endParam: normRad(bestParam) }
    : { startParam: normRad(bestParam), endParam: ell.endParam };
}
